import React, { useRef } from "react";
import { Upload, Plus, FileSpreadsheet, UploadCloud } from "lucide-react";
import Button from "./ui/Button";

const FileUploader = ({
  onFileUpload,
  isLoading = false,
  disabled = false,
  variant = "primary",
  showLabel = true,
}) => {
  const fileInputRef = useRef(null);

  const handleClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = (event) => {
    onFileUpload(event);
    // Reset the input so the same file can be selected again
    event.target.value = "";
  };

  if (variant === "compact") {
    return (
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
        <Button
          onClick={handleClick}
          disabled={isLoading || disabled}
          variant="secondary"
          size="sm"
          className="flex items-center space-x-2 bg-white hover:bg-gray-50 border border-gray-200 text-gray-700"
        >
          <Plus className="h-4 w-4" />
          <span>Add CSV</span>
        </Button>
      </div>
    );
  }

  if (variant === "floating") {
    return (
      <div className="relative">
        <input
          ref={fileInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleFileChange}
          className="hidden"
        />
        <button
          onClick={handleClick}
          disabled={isLoading || disabled}
          className="fixed bottom-6 right-6 w-14 h-14 bg-blue-500 hover:bg-blue-600 disabled:opacity-50 text-white rounded-full shadow-xl hover:shadow-2xl transition-all duration-200 flex items-center justify-center z-50 hover:scale-110"
        >
          {isLoading ? (
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-white border-t-transparent"></div>
          ) : (
            <Upload className="h-6 w-6" />
          )}
        </button>
      </div>
    );
  }

  // Default primary variant (for empty state)
  return (
    <div className="flex flex-col items-center justify-center text-center p-4">
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.xlsx,.xls"
        onChange={handleFileChange}
        className="hidden"
      />

      <div className="space-y-4">
        {/* Upload Icon */}
        <div className="mx-auto w-20 h-20 bg-gradient-to-br from-[#B8460E]/20 to-transparent rounded-2xl flex items-center justify-center border border-white/10 shadow-lg">
          {isLoading ? (
            <div className="animate-spin rounded-full h-10 w-10 border-2 border-white border-t-transparent"></div>
          ) : (
            <UploadCloud className="h-10 w-10 text-white/80" />
          )}
        </div>

        {/* Text Content */}
        <div className="space-y-1">
          <h3 className="text-xl font-semibold text-white">
            {isLoading ? "Processing..." : "Upload CSV Data"}
          </h3>
          <p className="text-gray-400 max-w-sm mx-auto text-sm">
            {isLoading
              ? "Saving your data to the database..."
              : "Drop your CSV file here or click to browse. Data will be added to your existing database."}
          </p>
        </div>

        {/* Upload Button */}
        <div className="space-y-2 pt-2">
          <Button
            onClick={handleClick}
            disabled={isLoading || disabled}
            variant="primary"
            className="bg-white/10 hover:bg-white/20 border border-white/20 backdrop-blur-xl text-white rounded-xl shadow-lg hover:shadow-xl transition-all"
          >
            <FileSpreadsheet className="h-5 w-5 mr-2" />
            {isLoading ? "Processing..." : "Choose CSV File"}
          </Button>

          {showLabel && (
            <p className="text-xs text-gray-500">
              Supports .csv files with ad performance data
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default FileUploader;
